import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Icon } from '../Icon/Icon.js';
import { parseColor, generateP3CSS } from '../../utils/colorUtils.js';
import '../DropdownMenu/DropdownMenu.css';
import './Select.css';

export const Select = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select a color...',
  label = '',
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  type = 'default', // 'default', 'color'
  style = 'outline', // 'outline', 'ghost'
  className = '',
  labelClassName = '',
  showSectionHeaders = true, // Add showSectionHeaders prop
  primitivesSectionTitle = 'Primitives',
  themesSectionTitle = 'Themes',
  warning = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [scrollTop, setScrollTop] = useState(0);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const listInnerRef = useRef(null);

  const OPTION_ROW_HEIGHT = 32;
  const VIRTUAL_THRESHOLD = 80;
  const useVirtualList = !showSectionHeaders && filteredOptions.length > VIRTUAL_THRESHOLD;

  // Update filtered options when options or search query change
  useEffect(() => {
    if (!searchable || !searchQuery.trim()) {
      setFilteredOptions(options);
    } else {
      const normalize = (str) => (str || '')
        .toString()
        .toLowerCase()
        .replace(/[._-]+/g, ' ')      // treat separators as spaces
        .replace(/\//g, ' ')          // treat path slashes as spaces
        .replace(/[^a-z0-9\s]+/g, ' ')// strip other symbols
        .replace(/\s+/g, ' ')         // collapse whitespace
        .trim();

      const tokens = normalize(searchQuery).split(' ').filter(Boolean);

      const filtered = options.filter(option => {
        const parts = [option.label, option.folderName, option.folderPath].filter(Boolean);
        const target = normalize(parts.join(' '));
        // Every token from the query must appear somewhere in the combined name
        return tokens.every(tok => target.includes(tok));
      });
      setFilteredOptions(filtered);
    }
  }, [options, searchQuery, searchable]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (!isOpen || !dropdownRef.current || !value) return;
    const timeoutId = setTimeout(() => {
      const scrollEl = useVirtualList ? listInnerRef.current : dropdownRef.current;
      const selectedElement = dropdownRef.current?.querySelector('[aria-selected="true"]');
      if (selectedElement && scrollEl) {
        const elementOffsetTop = selectedElement.offsetTop;
        const elementHeight = selectedElement.offsetHeight;
        const dropdownHeight = scrollEl.clientHeight;
        const newScrollTop = Math.max(0, elementOffsetTop - (dropdownHeight / 2) + (elementHeight / 2));
        scrollEl.scrollTop = newScrollTop;
        if (useVirtualList) setScrollTop(newScrollTop);
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [isOpen, value, useVirtualList]);

  // Track scroll position for virtual list
  useEffect(() => {
    if (!useVirtualList || !isOpen) return;
    const scrollEl = listInnerRef.current;
    if (!scrollEl) return;
    const onScroll = () => setScrollTop(scrollEl.scrollTop);
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [useVirtualList, isOpen]);

  const baseClass = 'select';
  const typeClass = `select--${type}`;
  const styleClass = `select--${style}`;
  const disabledClass = disabled ? 'select--disabled' : '';
  const openClass = isOpen ? 'select--open' : '';
  const emptyClass = !value ? 'select--empty' : '';

  const combinedClassName = [
    baseClass,
    typeClass,
    styleClass,
    disabledClass,
    openClass,
    emptyClass,
    className
  ].filter(Boolean).join(' ');

  // Add ghost wrapper class for content-based width
  const wrapperClassName = style === 'ghost' ? 'select-wrapper select-wrapper--fit-conten' : 'select-wrapper';

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && searchable) {
        // Clear search when opening
        setSearchQuery('');
      }
    }
  };

  const handleOptionClick = (option) => {
    if (!disabled && onChange) {
      onChange(option);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleSearchInputClick = (e) => {
    // Prevent dropdown from closing when clicking search input
    e.stopPropagation();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Organize options by folders for better visual organization
  const organizeOptionsByFolders = (options) => {
    const organized = {
      primitives: {
        unfoldered: [],
        folders: {}
      },
      tokens: {
        unfoldered: [],
        folders: {}
      },
      waitlist: {
        unfoldered: [],
        folders: {}
      },
      // Track if any option explicitly belongs to the waitlist section
      hasWaitlist: false
    };

    // First pass: collect all options and organize them by section
    options.forEach(option => {
      const section = option.section || (option.isToken ? 'tokens' : 'primitives');
      if (section === 'waitlist') organized.hasWaitlist = true;
      const sectionData = organized[section];
      
      if (option.folderId && option.folderName) {
        if (!sectionData.folders[option.folderId]) {
          sectionData.folders[option.folderId] = {
            name: option.folderPath || option.folderName,
            options: []
          };
        }
        sectionData.folders[option.folderId].options.push(option);
      } else {
        sectionData.unfoldered.push(option);
      }
    });

    // Second pass: preserve the original order by using the order from the options array
    // Create ordered lists of folders for each section
    const primitivesFolderOrder = [];
    const tokensFolderOrder = [];
    const waitlistFolderOrder = [];
    const seenPrimitivesFolders = new Set();
    const seenTokensFolders = new Set();
    const seenWaitlistFolders = new Set();
    
    options.forEach(option => {
      const section = option.section || (option.isToken ? 'tokens' : 'primitives');
      if (option.folderId && option.folderName) {
        if (section === 'primitives' && !seenPrimitivesFolders.has(option.folderId)) {
          primitivesFolderOrder.push(option.folderId);
          seenPrimitivesFolders.add(option.folderId);
        } else if (section === 'tokens' && !seenTokensFolders.has(option.folderId)) {
          tokensFolderOrder.push(option.folderId);
          seenTokensFolders.add(option.folderId);
        } else if (section === 'waitlist' && !seenWaitlistFolders.has(option.folderId)) {
          waitlistFolderOrder.push(option.folderId);
          seenWaitlistFolders.add(option.folderId);
        }
      }
    });

    // Add the folder orders to the organized object
    organized.primitives.folderOrder = primitivesFolderOrder;
    organized.tokens.folderOrder = tokensFolderOrder;
    organized.waitlist.folderOrder = waitlistFolderOrder;

    return organized;
  };

  const organizedOptions = useMemo(
    () => (showSectionHeaders ? organizeOptionsByFolders(filteredOptions) : null),
    [showSectionHeaders, filteredOptions]
  );

  // Shared option renderer — single source for every option row
  const renderOption = (option, index) => {
    const isSelected = option.value === value;
    return (
      <div
        key={option.value || index}
        className={`dropdown-menu__item select__option${isSelected ? ' dropdown-menu__item--selected' : ''}`}
        onClick={() => handleOptionClick(option)}
        role="option"
        aria-selected={isSelected}
      >
        {renderColorSwatch(option, 'select__option-swatch')}
        <span className="dropdown-menu__item-label select__option-text">{option.label}</span>
        {isSelected && (
          <Icon name="check" size="small" className="dropdown-menu__item-check" />
        )}
      </div>
    );
  };

  const DROPDOWN_MAX_HEIGHT = 320;
  const overscan = 5;
  const virtualRange = useVirtualList
    ? (() => {
        const start = Math.max(0, Math.floor(scrollTop / OPTION_ROW_HEIGHT) - overscan);
        const end = Math.min(filteredOptions.length - 1, Math.ceil((scrollTop + DROPDOWN_MAX_HEIGHT) / OPTION_ROW_HEIGHT) + overscan);
        return { start, end };
      })()
    : { start: 0, end: filteredOptions.length - 1 };

  // Render simple options without section headers (optionally virtualized when many options)
  const renderSimpleOptions = () => {
    if (useVirtualList) {
      const totalHeight = filteredOptions.length * OPTION_ROW_HEIGHT;
      const { start, end } = virtualRange;
      const visibleOptions = filteredOptions.slice(start, end + 1);
      return (
        <div
          className="select__dropdown-list-inner"
          ref={listInnerRef}
          style={{ overflow: 'auto', maxHeight: DROPDOWN_MAX_HEIGHT }}
          role="listbox"
        >
          <div style={{ height: totalHeight, position: 'relative' }}>
            {visibleOptions.map((option, i) => {
              const index = start + i;
              return (
                <div
                  key={option.value ?? index}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: index * OPTION_ROW_HEIGHT,
                    height: OPTION_ROW_HEIGHT,
                  }}
                >
                  {renderOption(option, index)}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return filteredOptions.map((option, index) => renderOption(option, index));
  };

  // Render options with section and folder headers
  const renderOptionsWithFolders = () => {
    const organized = organizedOptions || organizeOptionsByFolders(filteredOptions);
    const sections = [];

    // Helper function to render a section
    const renderSection = (sectionName, sectionData, sectionKey) => {
      const sectionSections = [];
      
      // Add unfoldered options first
      if (sectionData.unfoldered.length > 0) {
        sectionSections.push(
          <div key={`${sectionKey}-unfoldered`}>
            {sectionData.unfoldered.map((option, index) => renderOption(option, index))}
          </div>
        );
      }

      // Add folder sections in the correct order
      if (sectionData.folderOrder) {
        sectionData.folderOrder.forEach(folderId => {
          const folderData = sectionData.folders[folderId];
          if (folderData) {
            sectionSections.push(
              <div key={folderId}>
                <div className="select__folder-header">
                  {folderData.name}
                </div>
                {folderData.options.map((option, index) => renderOption(option, index))}
              </div>
            );
          }
        });
      }

      // Only add section header if there are items in this section
      if (sectionSections.length > 0) {
        sections.push(
          <div key={sectionKey}>
            <div className="select__section-header">
              {sectionName}
            </div>
            {sectionSections}
          </div>
        );
      }
    };

    // Render Waitlist section first (no section header)
    if (organized.waitlist.folderOrder.length > 0 || organized.waitlist.unfoldered.length > 0) {
      const waitlistSections = [];
      
      // Render folders in order
      organized.waitlist.folderOrder.forEach(folderId => {
        const folderData = organized.waitlist.folders[folderId];
        if (folderData && folderData.options.length > 0) {
          waitlistSections.push(
            <div key={folderId}>
              <div className="select__folder-header">
                {folderData.name}
              </div>
              {folderData.options.map((option, index) => renderOption(option, index))}
            </div>
          );
        }
      });
      
      // Render unfoldered items
      if (organized.waitlist.unfoldered.length > 0) {
        waitlistSections.push(
          <div key="waitlist-unfoldered">
            {organized.waitlist.unfoldered.map((option, index) => renderOption(option, index))}
          </div>
        );
      }
      
      if (waitlistSections.length > 0) {
        sections.push(
          <div key="waitlist">
            {waitlistSections}
          </div>
        );
      }
    }
    
    // Only render Primitives and Themes sections if there are no waitlist colors
    const hasWaitlistColors = organized.hasWaitlist || organized.waitlist.folderOrder.length > 0 || organized.waitlist.unfoldered.length > 0;
    
    // Debug logging
    
    if (!hasWaitlistColors) {
      // Render Primitives section second (use actual collection name)
      renderSection(primitivesSectionTitle, organized.primitives, 'primitives');
      
      // Render Themes section third (use actual collection name)
      renderSection(themesSectionTitle, organized.tokens, 'tokens');
    }

    return sections;
  };

  // Helper function to render proper color swatch with transparency support
  const renderColorSwatch = (option, additionalClassName = '') => {
    if (type !== 'color' || !option?.color) {
      return null;
    }
    
    const baseClass = additionalClassName || 'select__swatch';
    
    // Use precomputed displayCss when available to avoid re-parsing and to include alpha & P3
    // Falls back to hex parsed from option.color for compatibility
    let backgroundColor = option.displayCss;
    if (!backgroundColor) {
      if (option.color && option.color.startsWith('oklch(')) {
        try {
          const parsedColor = parseColor(option.color);
          if (parsedColor.isValid) {
            backgroundColor = generateP3CSS(parsedColor);
          }
        } catch (error) {
          console.warn('Failed to parse OKLCH color for swatch:', option.color, error);
          backgroundColor = '#808080';
        }
      } else {
        backgroundColor = option.color;
      }
    }
    
    // Always show transparency pattern underneath; it only shows through for colors with alpha
    return (
      <div className={baseClass}>
        <div 
          className="select__swatch-color"
          style={{ background: backgroundColor }}
        />
      </div>
    );
  };

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption
    ? (selectedOption.folderName
        ? `${selectedOption.folderName}/${selectedOption.label}`
        : selectedOption.label)
    : placeholder;

  // Check if color select is empty/invalid (no selected option or no valid color)
  const isEmptyColorSelect = type === 'color' && (!selectedOption || !selectedOption.color);
  const showWarning = warning || isEmptyColorSelect;
  const warningClass = showWarning ? 'select--warning' : '';

  return (
    <div className={wrapperClassName} ref={selectRef}>
      {label && (
        <label className={`select__label ${labelClassName}`}>
          {label}
        </label>
      )}
      <div
        className={`${combinedClassName} ${warningClass}`}
        onClick={handleToggle}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        {...props}
      >
        {/* Warning icon when warning state, otherwise color swatch (color type only) */}
        {showWarning ? (
          <Icon name="warning" size="small" className="select__warning-icon" />
        ) : (
          renderColorSwatch(selectedOption)
        )}
        
        {/* Select text */}
        <span className="select__text">{displayValue}</span>
        
        {/* Dropdown arrow */}
        <Icon name="expand-more" size="small" className="select__arrow" />
      </div>

      {/* Dropdown options */}
      {isOpen && (
        <div className="dropdown-menu dropdown-menu--align-left select__dropdown" ref={dropdownRef}>
          {searchable && (
            <div className="select__search-container">
              <input
                type="text"
                className="select__search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={handleSearchInputClick}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length === 0 && searchable && searchQuery ? (
            <div className="select__no-results">No colors found</div>
          ) : (
            showSectionHeaders ? renderOptionsWithFolders() : renderSimpleOptions()
          )}
        </div>
      )}
    </div>
  );
};
